import { Head, Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CategoryOption {
  value: number
  label: string
}

interface Props {
  service_item_type: {
    name: string
    price: number | null
    category: number
  }
  category_options: CategoryOption[]
  errors?: Record<string, string[]>
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/service_item_types" },
  { title: "Service Items", href: "/admin/service_item_types" },
  { title: "Add", href: "/admin/service_item_types/new" },
]

export default function New({
  service_item_type,
  category_options,
  errors,
}: Props) {
  const { data, setData, post, processing, transform } = useForm({
    name: service_item_type.name,
    price: service_item_type.price ?? "",
    category: service_item_type.category,
  })

  transform((data) => ({ service_item_type: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post("/admin/service_item_types")
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Add Service Item" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Add Service Item</h1>

        <p className="text-hpi-red mb-4 text-sm font-medium">
          Don&apos;t re-use service items — changing them will affect your case
          history.
        </p>

        <form onSubmit={handleSubmit} className="max-w-md">
          <div className="mb-4">
            <label htmlFor="category" className="mb-1 block font-medium">
              Type
            </label>
            <select
              id="category"
              value={data.category}
              onChange={(e) => setData("category", parseInt(e.target.value))}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            >
              {category_options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label htmlFor="name" className="mb-1 block font-medium">
              Name
            </label>
            <input
              type="text"
              id="name"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={50}
              required
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.name.join(", ")}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="price" className="mb-1 block font-medium">
              Price (optional)
            </label>
            <input
              type="number"
              id="price"
              value={data.price}
              onChange={(e) => setData("price", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              step="0.01"
              min="0"
              placeholder="0.00"
            />
            {errors?.price && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.price.join(", ")}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              Add
            </button>
            <Link
              href="/admin/service_item_types"
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </Link>
          </div>
        </form>

        <p className="text-hpi-med-gray mt-4 text-sm">
          <strong>Type:</strong> Controls how time shows on billing &amp;
          payroll reports
        </p>
      </div>
    </AppLayout>
  )
}
